import { sha256 } from 'crypto-hash';
import { useState } from 'react';

import { initializeApollo } from '@/graphql/client';
import type {
  CheckoutLineFragment,
  CheckoutQuery,
  CheckoutQueryVariables,
  OrderFragment,
} from '@/graphql/saleor/api';
import { CheckoutDocument } from '@/graphql/saleor/api';
import { useAnalyticsExperimentAssignments } from '@/hooks/useAnalyticsExperimentAssignments';
import { checkAnalyticsConsent } from '@/lib/analyticsConsent';
import { checkoutRestore } from '@/lib/checkout';
import {
  GA_DEBUG_MODE,
  NEXT_PUBLIC_IMAGES_FORMAT,
  THUMBNAIL_IMAGE_SIZE,
} from '@/lib/const';
import { captureError, isAbortLikeError } from '@/lib/errors';
import { getCountryIP } from '@/lib/geoCountry';
import { EVENT_NAME } from '@/lib/gtm/const';
import { getGTMEventQueue } from '@/lib/gtm/eventQueue';
import {
  createEcommerce,
  createEcommerceBySkus,
  extractCheckoutLinesFromOrder,
  getMarketEcom,
  getMenuCategory,
  getPageType,
  sanitizeCountryCode,
} from '@/lib/gtm/helpers';
import type {
  BaseEvent,
  Contact,
  CtaDiscover,
  EcommerceEvent,
  EcommercePayload,
  GenerateLead,
  Item,
  LocatorPinDetails,
  LoginEvent,
  NewsletterSubscription,
  PersonalAideEvent,
  PopinView,
  SearchEvent,
  ServiceComplete,
  ServiceReason,
  ServiceRequest,
  ServieShipping,
  SignUpEvent,
  VideoParams,
  WishlistComparisonStart,
} from '@/lib/gtm/types';
import type { PaymentMethod } from '@/lib/payment/types';
import { useRegionProvider } from '@/providers/RegionProvider';
import { useGtmEnabled } from '@/providers/ThirdPartyFlagsProvider/ThirdPartyFlagsContext';
import { usePageReferrerState } from '@/states/pageReferrerState';

import { useRouter } from './useRouter';
import { useUser } from './useUser';

/** Async noop matching the sendEvent signature — used when GTM is disabled. */
const asyncNoop = (): Promise<void> => Promise.resolve();
/** Sync noop — used for non-async GTM functions when GTM is disabled. */
// eslint-disable-next-line @typescript-eslint/no-empty-function
const syncNoop = (): void => {};

export const useGoogleAnalytics = () => {
  const isGtmEnabled = useGtmEnabled();
  const experimentAssignments = useAnalyticsExperimentAssignments();
  const {
    country,
    locale,
    query: { channel, languageCode },
  } = useRegionProvider();
  const { fetchUser, snapshot } = useUser();
  const { asPath, pathname } = useRouter();
  const { pageReferrer, prevUrl } = usePageReferrerState();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [pushedSkus, setPushedSkus] = useState<string[]>([]);

  // Only after all hooks: return noops when GTM is disabled.
  if (!isGtmEnabled) {
    return {
      sendEvent: asyncNoop,
      sendEcommerceEvent: syncNoop,
      sendGlobaleEvent: syncNoop,
      sendShippingInfoEvent: asyncNoop,
      sendPurchaseEvent: asyncNoop,
      sendAddPaymentInfoEvent: asyncNoop,
      sendEcommerceEventByLines: asyncNoop,
      sendEcommerceEventBySkus: asyncNoop,
      sendRegisteredWatchEvent: syncNoop,
      sendGenerateLeadEvent: syncNoop,
      sendViewItemListEvent: syncNoop,
      sendPromotionEvent: syncNoop,
      sendWishlistComparisonStartEvent: syncNoop,
    };
  }

  const sendEvent = async (
    data: Partial<
      | BaseEvent
      | EcommerceEvent
      | LoginEvent
      | SignUpEvent
      | SearchEvent
      | PopinView
      | WishlistComparisonStart
      | NewsletterSubscription
      | ServiceComplete
      | GenerateLead
      | CtaDiscover
      | LocatorPinDetails
      | ServiceRequest
      | ServiceReason
      | ServieShipping
      | VideoParams
      | Contact
      | PersonalAideEvent
    >,
  ) => {
    let user = snapshot.user;
    if (snapshot.isAuthenticated === null) user = await fetchUser();
    let isAuthenticated = false;
    if (user) isAuthenticated = true;
    const userHashedEmail = user?.email ? await sha256(user.email) : undefined;

    const cid = user?.metadata?.find(
      (item) => item.key === 'user.salesforce_id',
    )?.value;

    if (GA_DEBUG_MODE) {
      console.log('GA4 Debug - User Identification:', {
        isAuthenticated: snapshot.isAuthenticated,
        hasUser: !!user,
        userId: user?.id,
        cid: cid,
        userMetadataCount: user?.metadata?.length || 0,
        userEmail: user?.email,
        userMetadata:
          user?.metadata?.map((m) => ({ key: m.key, hasValue: !!m.value })) ||
          [],
      });
    }

    const pathSplits = asPath.split('/').filter(Boolean);
    const mainPath = `/${pathSplits.slice(1).join('/')}`;

    const baseData: BaseEvent = {
      event: EVENT_NAME.PAGE_VIEW,
      user_status: isAuthenticated ? 'yes' : 'no',
      user_id: cid,
      user_hashed_email: userHashedEmail,
      page_type: getPageType(mainPath, pathname, isAuthenticated),
      page_referrer: prevUrl || document.referrer || pageReferrer,
      menu_category: getMenuCategory(mainPath),
      country: sanitizeCountryCode(country.code),
      ...(checkAnalyticsConsent()
        ? {
            countryIP: getCountryIP(),
            experiments: experimentAssignments,
          }
        : {}),
      market_ecom: getMarketEcom(country.name),
      language: locale.slug,
      ...data,
    };

    if (typeof window !== 'undefined') {
      window.dataLayer = window.dataLayer || [];
    }

    // Use event queue to ensure proper ordering
    // ALL events except page_view itself must wait for page_view to fire first
    const queue = getGTMEventQueue();
    const isPageView = baseData.event === EVENT_NAME.PAGE_VIEW;
    const needsPageViewFirst = !isPageView; // Everything except page_view waits

    queue.pushEvent(baseData, needsPageViewFirst);
  };

  const sendEcommerceEvent = (
    data: Partial<EcommerceEvent | GenerateLead | WishlistComparisonStart>,
  ) => {
    const queue = getGTMEventQueue();
    queue.pushEvent({ ecommerce: null }, false);
    sendEvent(data);
  };

  const sendShippingInfoEvent = async ({
    coupon,
    deliveryOption,
    isGift,
    lines,
    totalValue,
  }: {
    coupon?: string;
    deliveryOption: string;
    isGift: boolean;
    lines: CheckoutLineFragment[];
    totalValue: number;
  }) => {
    try {
      const ecommerce: EcommercePayload = await createEcommerce({
        lines,
        channel,
        languageCode,
        coupon,
        totalValue,
      });

      sendEcommerceEvent({
        event: EVENT_NAME.ADD_SHIPPING_INFO,
        gift_option: isGift ? 'yes' : 'no',
        ecommerce: {
          ...ecommerce,
          shipping_tier: deliveryOption,
        },
      });
    } catch (error) {
      captureError(new Error('sendShippingInfoEvent failed'), {
        extra: {
          error,
        },
      });
    }
  };

  const sendAddPaymentInfoEvent = async ({
    coupon,
    currency,
    lines,
    paymentType,
    value,
  }: {
    coupon?: string;
    currency: string;
    lines: CheckoutLineFragment[];
    paymentType: string[];
    value: number;
  }) => {
    try {
      const ecommerce: EcommercePayload = await createEcommerce({
        coupon,
        lines,
        channel,
        languageCode,
        totalValue: value,
      });

      sendEcommerceEvent({
        event: EVENT_NAME.ADD_PAYMENT_INFO,
        ecommerce: {
          ...ecommerce,
          currency,
          payment_type: paymentType,
        },
      });
    } catch (error) {
      captureError(new Error('sendAddPaymentInfoEvent failed'), {
        extra: {
          error,
        },
      });
    }
  };

  const sendPurchaseEvent = async (
    order: OrderFragment,
    orderId: string,
    orderPaymentData?: (Partial<PaymentMethod> | null | undefined)[],
  ) => {
    const {
      total: {
        tax: { amount: taxAmount },
      },
      totalAuthorized: { amount, currency },
      voucherCode,
    } = order;

    try {
      const ecommerce: EcommercePayload = await createEcommerce({
        coupon: voucherCode ?? undefined,
        lines: extractCheckoutLinesFromOrder(order),
        channel,
        languageCode,
        totalValue: amount,
      });

      const paymentMethods = orderPaymentData?.map(
        (payment) => payment?.type,
      ) as string[];

      sendEcommerceEvent({
        event: EVENT_NAME.PURCHASE,
        ecommerce: {
          ...ecommerce,
          value: amount,
          shipping: 0,
          currency,
          tax: taxAmount,
          transaction_id: orderId,
          payment_type: paymentMethods,
        },
      });
    } catch (error) {
      captureError(new Error('sendPurchaseEvent failed'), {
        extra: {
          error,
        },
      });
    }
  };

  const sendEcommerceEventByLines = async ({
    coupon,
    event,
    lines,
    totalValue,
  }: {
    coupon?: string;
    event: EVENT_NAME;
    lines: CheckoutLineFragment[];
    totalValue?: number;
  }) => {
    try {
      const ecommerce: EcommercePayload = await createEcommerce({
        coupon,
        lines,
        channel,
        languageCode,
        isCurrency: !(event === EVENT_NAME.INTENTION_VIEW_CART),
        totalValue,
      });

      sendEcommerceEvent({
        event,
        ecommerce,
      });
    } catch (error) {
      captureError(new Error('sendEcommerceEventByLines failed'), {
        extra: {
          error,
        },
      });
    }
  };

  const sendEcommerceEventBySkus = async ({
    event,
    itemListName,
    skus,
  }: {
    event: EVENT_NAME;
    itemListName?: string;
    skus: string[];
  }) => {
    try {
      const ecommerce: EcommercePayload = await createEcommerceBySkus({
        skus,
        channel,
        languageCode,
        itemListName,
      });

      sendEcommerceEvent({
        event,
        ecommerce,
      });
    } catch (error) {
      if (isAbortLikeError(error)) {
        return;
      }
      captureError(new Error('sendEcommerceEventBySkus failed'), {
        extra: {
          error,
        },
      });
    }
  };

  const sendRegisteredWatchEvent = async (sku: string) => {
    try {
      const ecommerce: EcommercePayload = await createEcommerceBySkus({
        skus: [sku],
        channel,
        languageCode,
      });

      const items: Item[] = ecommerce.items?.map((el) => ({
        item_id: el.item_id,
        item_name: el.item_name,
        item_category: el.item_category,
        item_collection: el.item_collection,
        item_gender: el.item_gender,
      })) as Item[];

      sendEcommerceEvent({
        event: EVENT_NAME.REGISTERED_WATCH,
        ecommerce: {
          items,
        },
      });
    } catch (error) {
      captureError(new Error('sendRegisteredWatchEvent failed'), {
        extra: {
          error,
        },
      });
    }
  };

  const sendGenerateLeadEvent = async ({
    appointmentId,
    appointmentMotive,
    dateAppointment,
    sku,
    storeId,
  }: {
    appointmentId: string;
    appointmentMotive: string;
    dateAppointment: string;
    sku?: string;
    storeId: string;
  }) => {
    try {
      let items: Item[] = [];
      if (sku) {
        const ecommerce: EcommercePayload = await createEcommerceBySkus({
          skus: [sku],
          channel,
          languageCode,
        });
        items = ecommerce.items as Item[];
      }

      sendEcommerceEvent({
        event: EVENT_NAME.GENERATE_LEAD,
        appointment_motive: appointmentMotive,
        appointment_id: appointmentId,
        date_appointment: dateAppointment,
        store_id: storeId,
        ecommerce: {
          items,
        },
      });
    } catch (error) {
      captureError(new Error('sendGenerateLeadEvent failed'), {
        extra: {
          error,
        },
      });
    }
  };

  const sendViewItemListEvent = ({
    itemListName,
    skus,
  }: {
    itemListName?: string;
    skus: string[];
  }) => {
    setPushedSkus((prev) => {
      const _skus = skus.filter((el) => !prev?.includes(el) && el !== '');

      if (_skus.length > 0) {
        sendEcommerceEventBySkus({
          event: EVENT_NAME.VIEW_ITEM_LIST,
          skus: skus.length > 0 ? skus : [''],
          itemListName,
        });
        return [...prev, ..._skus];
      }
      return [...prev];
    });
  };

  const sendPromotionEvent = async ({
    creativeName,
    creativeSlot,
    event,
    sku,
  }: {
    creativeName: string;
    creativeSlot: number;
    event: EVENT_NAME;
    sku?: string;
  }) => {
    try {
      let items: Item[] = [];
      if (sku) {
        const ecommerce: EcommercePayload = await createEcommerceBySkus({
          skus: [sku],
          channel,
          languageCode,
        });
        items = ecommerce.items as Item[];
      }

      sendEcommerceEvent({
        event,
        ecommerce: {
          creative_slot: creativeSlot,
          creative_name: creativeName,
          items,
        },
      });
    } catch (error) {
      captureError(new Error('sendPromotionEvent failed'), {
        extra: {
          error,
        },
      });
    }
  };

  const sendWishlistComparisonStartEvent = async ({
    skus,
    wishlistNumberProductsCompared,
  }: {
    skus: string[];
    wishlistNumberProductsCompared: number;
  }) => {
    try {
      const ecommerce: EcommercePayload = await createEcommerceBySkus({
        skus: [...skus],
        channel,
        languageCode,
      });

      sendEcommerceEvent({
        event: EVENT_NAME.WISHLIST_COMPARISON_START,
        wishlist_number_products_compared: wishlistNumberProductsCompared,
        ecommerce,
      });
    } catch (error) {
      captureError(new Error('sendWishlistComparisonStartEvent failed'), {
        extra: {
          error,
        },
      });
    }
  };

  const sendGlobaleEvent = async (
    event: string,
    checkoutId?: string | null,
    options?: {
      deliveryOption?: string;
      isGift?: boolean;
      payment_type?: string;
    },
  ) => {
    if (!checkoutId) return;

    const { paymentMethod } = await checkoutRestore({
      languageCode: locale.code,
      id: checkoutId,
      imagesFormat: NEXT_PUBLIC_IMAGES_FORMAT,
      thumbnailSize: THUMBNAIL_IMAGE_SIZE,
    });

    const client = initializeApollo();
    const { data } = await client.query<CheckoutQuery, CheckoutQueryVariables>({
      query: CheckoutDocument,
      variables: {
        id: checkoutId,
        languageCode: locale.code,
        imagesFormat: NEXT_PUBLIC_IMAGES_FORMAT,
        thumbnailSize: THUMBNAIL_IMAGE_SIZE,
      },
    });
    const checkout = data?.checkout;

    if (!checkout) return;

    const {
      lines,
      orderBid,
      totalPrice: {
        gross: { amount: grossAmount, currency },
        tax: { amount: taxAmount },
      },
      voucherCode,
    } = checkout;

    const ecommerce: EcommercePayload = await createEcommerce({
      coupon: voucherCode ?? undefined,
      lines,
      channel,
      languageCode,
      totalValue: grossAmount,
    });

    switch (event) {
      case EVENT_NAME.PURCHASE:
        sendEcommerceEvent({
          event,
          ecommerce: {
            ...ecommerce,
            value: grossAmount,
            shipping: 0,
            currency,
            tax: taxAmount,
            transaction_id: orderBid ?? checkout.id,
            payment_type: paymentMethod?.type ? [paymentMethod.type] : [],
          },
        });
        break;

      case EVENT_NAME.ADD_SHIPPING_INFO:
        sendEcommerceEvent({
          event: EVENT_NAME.ADD_SHIPPING_INFO,
          gift_option: options?.isGift ? 'yes' : 'no',
          ecommerce: {
            ...ecommerce,
            shipping_tier: options?.deliveryOption,
          },
        });
        break;

      case EVENT_NAME.ADD_PAYMENT_INFO:
        sendEcommerceEvent({
          event: EVENT_NAME.ADD_PAYMENT_INFO,
          gift_option: options?.isGift ? 'yes' : 'no',
          ecommerce: {
            ...ecommerce,
            value: grossAmount,
            currency,
            payment_type: options?.payment_type ? [options.payment_type] : [],
          },
        });
        break;
    }
  };

  return {
    sendEvent,
    sendEcommerceEvent,
    sendGlobaleEvent,
    sendShippingInfoEvent,
    sendPurchaseEvent,
    sendAddPaymentInfoEvent,
    sendEcommerceEventByLines,
    sendEcommerceEventBySkus,
    sendRegisteredWatchEvent,
    sendGenerateLeadEvent,
    sendViewItemListEvent,
    sendPromotionEvent,
    sendWishlistComparisonStartEvent,
  };
};
